import { Box, Tag, TagLabel } from "@chakra-ui/react";
import { Tooltip } from "components";
import { EnabledOrDisabledTag } from "components/tags/EnabledOrDisabledTag";
import { DefaultHeaderCell } from "components/table-v2/header-cells";
import { DefaultRowCell } from "components/table-v2/row-cells";
import { ITableV2Column } from "components/table-v2/table-v2";
import { FC } from "react";
import { TAccountLeadInactivity } from "types/account-lead-inactivity.type";
import { getLeadTypeLabel } from "utils/lead-type.utils";
import { toSelectionLabels } from "utils/exclusive-option.utils";
import { LEAD_INACTIVITY_TYPE_LABELS } from "constants/lead-inactivity";
import { LeadInactivityTableActionCell } from "./LeadInactivityTableActionCell";

const UNAVAILABLE_OFFICE_HINT =
  "This office is no longer available. Edit to redirect to another office or delete this rule.";

const ALL_LEAD_TYPES_LABEL = "All";

const ALL_SOURCE_OFFICES_LABEL = "All";

const MAX_VISIBLE_CRITERIA_TAGS = 4;

interface ICriteriaTagListProps {
  labels: string[];
}

const CriteriaTagList: FC<ICriteriaTagListProps> = ({ labels }) => {
  const hiddenCount = labels.length - MAX_VISIBLE_CRITERIA_TAGS;
  const hiddenLabels = labels.slice(MAX_VISIBLE_CRITERIA_TAGS).join(", ");

  return (
    <DefaultRowCell height={"auto"} py={2}>
      <Box display={"flex"} gap={1} flexWrap={"wrap"}>
        {labels.slice(0, MAX_VISIBLE_CRITERIA_TAGS).map((label, index) => (
          <Tag key={index} size={"sm"} variant={"subtle"} bg={"leadpro.200"}>
            <TagLabel color={"leadpro.500"}>{label}</TagLabel>
          </Tag>
        ))}
        {hiddenCount > 0 && (
          <Tooltip label={hiddenLabels}>
            <Tag
              size={"sm"}
              variant={"subtle"}
              bg={"leadpro.200"}
              tabIndex={0}
              aria-label={hiddenLabels}
            >
              <TagLabel color={"leadpro.500"}>{`+${hiddenCount}`}</TagLabel>
            </Tag>
          </Tooltip>
        )}
      </Box>
    </DefaultRowCell>
  );
};

export const leadInactivityColumnDef = (
  getOfficeName: (officeId: number) => string,
  isOfficeMissing: (officeId: number) => boolean,
  handleEdit: (row: TAccountLeadInactivity) => () => void,
  handleRemove: (row: TAccountLeadInactivity) => () => void
): ITableV2Column<TAccountLeadInactivity>[] => {
  const describeOffice = (officeId: number) =>
    isOfficeMissing(officeId)
      ? `${getOfficeName(officeId)} (unavailable)`
      : getOfficeName(officeId);

  return [
    {
      accessor: "leadTypes",
      disableSortBy: true,
      Header: <DefaultHeaderCell>Lead types</DefaultHeaderCell>,
      Cell: ({ row }) => (
        <CriteriaTagList
          labels={toSelectionLabels(
            row.leadTypes,
            leadType => getLeadTypeLabel(leadType, true),
            ALL_LEAD_TYPES_LABEL
          )}
        />
      ),
    },
    {
      accessor: "sourceOfficeIds",
      disableSortBy: true,
      Header: <DefaultHeaderCell>Source offices</DefaultHeaderCell>,
      Cell: ({ row }) => (
        <CriteriaTagList
          labels={toSelectionLabels(
            row.sourceOfficeIds,
            describeOffice,
            ALL_SOURCE_OFFICES_LABEL
          )}
        />
      ),
    },
    {
      accessor: "delay",
      Header: <DefaultHeaderCell>Time limit (minutes)</DefaultHeaderCell>,
      Cell: ({ row }) => <DefaultRowCell>{row.delay}</DefaultRowCell>,
    },
    {
      accessor: "triggerWithinOfficeHours",
      width: "110px",
      Header: <DefaultHeaderCell>Office hours</DefaultHeaderCell>,
      Cell: ({ row }) => (
        <DefaultRowCell>
          {row.triggerWithinOfficeHours ? "Yes" : "No"}
        </DefaultRowCell>
      ),
    },
    {
      accessor: "type",
      disableSortBy: true,
      Header: <DefaultHeaderCell>Action</DefaultHeaderCell>,
      Cell: ({ row }) => (
        <DefaultRowCell>{LEAD_INACTIVITY_TYPE_LABELS[row.type]}</DefaultRowCell>
      ),
    },
    {
      accessor: "config",
      disableSortBy: true,
      Header: <DefaultHeaderCell>Redirect to</DefaultHeaderCell>,
      Cell: ({ row }) => (
        <DefaultRowCell>
          <Box display={"flex"} alignItems={"center"} gap={2} flexWrap={"wrap"}>
            <Box>{getOfficeName(row.config.officeId)}</Box>
            {isOfficeMissing(row.config.officeId) && (
              <Tooltip label={UNAVAILABLE_OFFICE_HINT}>
                <Tag
                  size={"sm"}
                  colorScheme={"red"}
                  tabIndex={0}
                  aria-label={`Unavailable. ${UNAVAILABLE_OFFICE_HINT}`}
                >
                  Unavailable
                </Tag>
              </Tooltip>
            )}
          </Box>
        </DefaultRowCell>
      ),
    },
    {
      accessor: "enabled",
      Header: <DefaultHeaderCell>Status</DefaultHeaderCell>,
      Cell: ({ row }) => (
        <DefaultRowCell>
          <EnabledOrDisabledTag isEnabled={row.enabled} />
        </DefaultRowCell>
      ),
    },
    {
      accessor: "id",
      width: "70px",
      disableSortBy: true,
      excludeFromGlobalFilter: true,
      Header: null,
      Cell: ({ row }) => (
        <LeadInactivityTableActionCell
          handleEdit={handleEdit(row)}
          handleRemove={handleRemove(row)}
        />
      ),
    },
  ];
};
